import { useEffect, useState } from 'preact/hooks';
import { Link } from 'wouter-preact';
import type { Overview } from '../../shared/types';
import { loadEnvelope, unlockWithPasskey, useVault } from '../lib/vault-store';
import { readPositions, summarize, type PositionsResult } from '../lib/positions';
import { PositionsTable } from './PositionsTable';

/** Dashboard block: locked/none states, or live positions once the vault is open. */
export function PositionsSection({ overview }: { overview: Overview }) {
  const vault = useVault();
  const [data, setData] = useState<PositionsResult | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => { loadEnvelope().catch((e: Error) => setError(e.message)); }, []);

  const addrKey = vault.addresses.map((a) => a.address).join(',');
  useEffect(() => {
    if (vault.status !== 'unlocked' || !overview.clientRpcUrl) return;
    setData(null);
    readPositions(overview.clientRpcUrl, overview.pools.map((p) => p.pool), vault.addresses)
      .then(setData)
      .catch((e: Error) => setError(`RPC read failed: ${e.message}`));
  }, [vault.status, addrKey, overview.clientRpcUrl]);

  async function unlock() {
    setBusy(true);
    setError(null);
    try { await unlockWithPasskey(); } catch (e) { setError((e as Error).message); } finally { setBusy(false); }
  }

  if (vault.status === 'loading') return null;
  if (vault.status === 'none') {
    return <p class="dash-note">Add your addresses in <Link href="/settings">Settings</Link> to see positions and exit coverage. They are encrypted in your browser.</p>;
  }
  if (vault.status === 'locked') {
    return (
      <div class="banner positions-locked" role="status">
        <span>Positions are locked.</span>
        <button class="btn" onClick={unlock} disabled={busy}>{busy ? 'Unlocking…' : 'Unlock with passkey'}</button>
        <Link href="/settings" class="dim">passphrase / settings</Link>
        {error && <span class="loss">{error}</span>}
      </div>
    );
  }
  if (error) return <div class="banner banner--critical" role="alert">{error}</div>;
  if (!overview.clientRpcUrl) return <div class="banner">CLIENT_RPC_URL is not configured.</div>;
  if (!data) return <p class="dash-note">Reading positions…</p>;
  return <PositionsTable summary={summarize(data.positions, overview.pools)} gas={data.gas} pools={overview.pools.map((p) => p.pool)} />;
}
